import React, { createContext, useContext, useState, useEffect } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Keep useApp for backward compatibility
export const useApp = useAppContext;

// Standard Rewards - Read-only system rewards
export const STANDARD_REWARDS = [
  {
    id: 'std_movie_night',
    name: 'Movie Night',
    description: 'Choose a family movie for the weekend',
    type: 'Special Outing',
    streakLength: 7,
    category: 'Entertainment'
  },
  {
    id: 'std_choose_dinner',
    name: 'Choose Dinner',
    description: 'Pick what the family has for dinner',
    type: 'Special Time',
    streakLength: 5,
    category: 'Food'
  },
  {
    id: 'std_extra_screen_time',
    name: '30 Minutes Extra Screen Time',
    description: 'Bonus screen time for games or videos',
    type: 'Screen Time',
    streakLength: 3,
    category: 'Entertainment'
  },
  {
    id: 'std_robux_gift_card',
    name: '£10 Robux Gift Card',
    description: 'Robux for Roblox games',
    type: 'Gift Card',
    streakLength: 14,
    category: 'Gaming'
  },
  {
    id: 'std_park_trip',
    name: 'Park Trip / Playground',
    description: 'Special trip to the park or playground',
    type: 'Special Outing',
    streakLength: 10,
    category: 'Outdoor'
  },
  {
    id: 'std_ice_cream_treat',
    name: 'Ice Cream Treat',
    description: 'Choose your favorite ice cream flavor',
    type: 'Gift',
    streakLength: 4,
    category: 'Food'
  },
  {
    id: 'std_board_game',
    name: 'Pick a Board Game',
    description: 'Choose which board game the family plays',
    type: 'Special Time',
    streakLength: 6,
    category: 'Games'
  },
  {
    id: 'std_stay_up_later',
    name: 'Stay Up 30 Minutes Later',
    description: 'Extended bedtime for one night',
    type: 'Special Time',
    streakLength: 8,
    category: 'Bedtime'
  }
];

export const AppProvider = ({ children }) => {
  const [appData, setAppData] = useState({
    children: [],
    sleep_logs: [],
    streak_milestones: [],
    rewards: [],
    earned_rewards: [],
    parents: {
      id: 1,
      name: 'Parent',
      email: 'parent@example.com',
      subscription: 'Premium',
      renewalDate: '2024-12-31'
    }
  });

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('sleepyRewardsData');
    if (savedData) {
      setAppData(JSON.parse(savedData));
    } else {
      // Initialize with sample data
      const sampleData = {
        children: [
          { 
            id: 1, 
            name: 'Emma', 
            age: 7, 
            avatarEmoji: '🌸',
            currentStreak: 0,
            totalGoodNights: 0,
            bestStreak: 0,
            milestones: [],
            earnedRewards: [],
            nextReward: { target: 7, reward: '1000 Robux' }
          },
          { 
            id: 2, 
            name: 'Oliver', 
            age: 5, 
            avatarEmoji: '🚀',
            currentStreak: 0,
            totalGoodNights: 0,
            bestStreak: 0,
            milestones: [],
            earnedRewards: [],
            nextReward: { target: 7, reward: 'Extra Story Time' }
          }
        ],
        sleep_logs: [
          { id: 1, childId: 1, date: format(new Date(Date.now() - 86400000), 'yyyy-MM-dd'), good: true, note: 'Great bedtime story!' },
          { id: 2, childId: 1, date: format(new Date(Date.now() - 2 * 86400000), 'yyyy-MM-dd'), good: true, note: '' },
          { id: 3, childId: 2, date: format(new Date(Date.now() - 86400000), 'yyyy-MM-dd'), good: false, note: 'Had trouble falling asleep' }
        ],
        streak_milestones: [
          { id: 1, childId: 1, days: 7, rewardId: 1, active: true },
          { id: 2, childId: 1, days: 14, rewardId: 2, active: true }
        ],
        rewards: [
          { id: 1, name: 'Extra Story Time', description: '15 minutes of extra bedtime stories', type: 'Special Time', streakLength: 7 },
          { id: 2, name: 'Movie Night', description: 'Choose a family movie for weekend', type: 'Special Outing', streakLength: 14 },
          { id: 3, name: 'New Book', description: 'Pick a new book from the bookstore', type: 'Gift', streakLength: 10 }
        ],
        earned_rewards: [],
        parents: {
          id: 1,
          name: 'Parent',
          email: 'parent@example.com',
          subscription: 'Premium',
          renewalDate: '2024-12-31'
        }
      };
      setAppData(sampleData);
    }
  }, []);

  // Save data to localStorage whenever appData changes
  useEffect(() => {
    localStorage.setItem('sleepyRewardsData', JSON.stringify(appData));
  }, [appData]);

  const addChild = (child) => {
    const newChild = {
      ...child,
      id: Date.now(),
      currentStreak: 0,
      totalGoodNights: 0,
      bestStreak: 0,
      milestones: [],
      earnedRewards: [],
      nextReward: { target: 7, reward: '1000 Robux' }
    };
    setAppData(prev => ({
      ...prev,
      children: [...prev.children, newChild]
    }));
  };

  const updateChild = (childId, updates) => {
    setAppData(prev => ({
      ...prev,
      children: prev.children.map(child =>
        child.id === childId ? { ...child, ...updates } : child
      )
    }));
  };

  const deleteChild = (childId) => {
    setAppData(prev => ({
      ...prev,
      children: prev.children.filter(child => child.id !== childId),
      sleep_logs: prev.sleep_logs.filter(log => log.childId !== childId),
      streak_milestones: prev.streak_milestones.filter(milestone => milestone.childId !== childId),
      earned_rewards: prev.earned_rewards.filter(reward => reward.childId !== childId)
    }));
  };

  const getChildById = (childId) => {
    return appData.children.find(child => child.id === parseInt(childId));
  };

  const addMilestone = (childId, milestone) => {
    const newMilestone = {
      id: Date.now(),
      target: milestone.target,
      reward: milestone.reward,
      rewardId: milestone.rewardId, // Support both reward text and rewardId
      description: milestone.description || ''
    };

    updateChild(childId, {
      ...getChildById(childId),
      milestones: [...(getChildById(childId)?.milestones || []), newMilestone]
    });
  };

  const deleteMilestone = (childId, milestoneId) => {
    const child = getChildById(childId);
    if (child) {
      updateChild(childId, {
        ...child,
        milestones: child.milestones.filter(m => m.id !== milestoneId)
      });
    }
  };

  const addSleepLog = (log) => {
    const newLog = {
      ...log,
      id: Date.now()
    };
    
    setAppData(prev => {
      const updatedLogs = prev.sleep_logs.filter(
        existingLog => !(existingLog.childId === log.childId && existingLog.date === log.date)
      );
      
      const newData = {
        ...prev,
        sleep_logs: [...updatedLogs, newLog]
      };

      // Update child stats
      const childLogs = newData.sleep_logs
        .filter(l => l.childId === log.childId)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      const stats = calculateChildStats(childLogs);
      
      // Update the child with new stats
      const updatedChildren = prev.children.map(child => 
        child.id === log.childId 
          ? { ...child, ...stats }
          : child
      );

      return {
        ...newData,
        children: updatedChildren
      };
    });
  };

  const removeSleepLog = (childId, date) => {
    setAppData(prev => ({
      ...prev,
      sleep_logs: prev.sleep_logs.filter(
        log => !(log.childId === childId && log.date === date)
      )
    }));
  };

  const calculateChildStats = (logs) => {
    if (!logs.length) return { currentStreak: 0, totalGoodNights: 0, bestStreak: 0 };
    
    const currentStreak = calculateCurrentStreak(logs);
    const totalGoodNights = logs.filter(log => log.good).length;
    
    // Calculate best streak
    let bestStreak = 0;
    let tempStreak = 0;
    
    [...logs].reverse().forEach(log => {
      if (log.good) {
        tempStreak++;
        bestStreak = Math.max(bestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    });

    return {
      currentStreak,
      totalGoodNights,
      bestStreak
    };
  };

  const calculateCurrentStreak = (logs) => {
    if (!logs.length) return 0;
    
    let streak = 0;
    for (const log of logs) {
      if (log.good) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const getChildStats = (childId) => {
    const childLogs = appData.sleep_logs
      .filter(log => log.childId === childId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return calculateChildStats(childLogs);
  };

  const addReward = (reward) => {
    const newReward = {
      ...reward,
      id: Date.now()
    };
    setAppData(prev => ({
      ...prev,
      rewards: [...prev.rewards, newReward]
    }));
  };

  const updateReward = (rewardId, updates) => {
    setAppData(prev => ({
      ...prev,
      rewards: prev.rewards.map(reward =>
        reward.id === rewardId ? { ...reward, ...updates } : reward
      )
    }));
  };

  const deleteReward = (rewardId) => {
    setAppData(prev => ({
      ...prev,
      rewards: prev.rewards.filter(reward => reward.id !== rewardId),
      streak_milestones: prev.streak_milestones.filter(milestone => milestone.rewardId !== rewardId)
    }));
  };

  const addStreakMilestone = (milestone) => {
    const newMilestone = {
      ...milestone,
      id: Date.now(),
      active: true
    };
    setAppData(prev => ({
      ...prev,
      streak_milestones: [...prev.streak_milestones, newMilestone]
    }));
  };

  const removeStreakMilestone = (milestoneId) => {
    setAppData(prev => ({
      ...prev,
      streak_milestones: prev.streak_milestones.filter(milestone => milestone.id !== milestoneId)
    }));
  };

  const markRewardAsDelivered = (earnedRewardId) => {
    setAppData(prev => ({
      ...prev,
      earned_rewards: prev.earned_rewards.map(reward =>
        reward.id === earnedRewardId 
          ? { ...reward, deliveredAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss') }
          : reward
      )
    }));
  };

  const updateParent = (updates) => {
    setAppData(prev => ({
      ...prev,
      parents: { ...prev.parents, ...updates }
    }));
  };

  // Helper function to get reward by ID (supports both standard and custom rewards)
  const getRewardById = (rewardId) => {
    // Check if it's a standard reward
    const standardReward = STANDARD_REWARDS.find(reward => reward.id === rewardId);
    if (standardReward) return standardReward;
    
    // Check custom rewards
    return appData.rewards.find(reward => reward.id === rewardId);
  };

  // Helper function to get all rewards (standard + custom)
  const getAllRewards = () => {
    return {
      standard: STANDARD_REWARDS,
      custom: appData.rewards
    };
  };

  const value = {
    ...appData,
    addChild,
    updateChild,
    deleteChild,
    getChildById,
    addMilestone,
    deleteMilestone,
    addSleepLog,
    removeSleepLog,
    getChildStats,
    addReward,
    updateReward,
    deleteReward,
    addStreakMilestone,
    removeStreakMilestone,
    markRewardAsDelivered,
    updateParent,
    getRewardById,
    getAllRewards,
    standardRewards: STANDARD_REWARDS
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};